<script lang="ts">
import { useProspect } from "~/composables/useProspect";
import { EntityTypeEnum } from "~~/enums/EntityType";

export default {
  data() {
    return {
      prospectManager: {},
      currentProspect: null as unknown as any | null,
      currentProspectType: null as unknown as ComputedRef<EntityTypeEnum>,
      prospectList: null as ComputedRef<any[] | null> | null,
    };
  },
  methods: {
    configureViewPort() {
      // If iOS, force maximimum scale to 1 to avoid auto zoom in in textfields
      if (/iPad|iPhone|iPod/.test(navigator.userAgent)) {
        document
          .querySelector("[name=viewport]")
          .setAttribute(
            "content",
            "width=device-width, initial-scale=1, maximum-scale=1"
          );
      }
    },
    async getAllData() {
      await useUser().loadUserInformation();
      this.currentProspectType = computed(() =>
        useProspect().getProspectType()
      );
      this.prospectList = await useProspect().getProspectList();
    },
    async getProspectManager() {
      if (this.currentProspect?.prospectId) {
        this.prospectManager = await useProspect().getProspectManager({
          prospectId: this.currentProspect.prospectId,
        });
      } else {
        this.prospectManager = await useProspect().getProspectManager({
          prospectType: this.currentProspectType,
        });
      }
    },
  },
  watch: {
    currentProspectType: {
      async handler(newValue, oldValue) {
        this.getProspectManager();
      },
    },
    prospectList: {
      async handler(newValue, oldValue) {
        if (newValue && newValue.prospects?.length > 0) {
          this.currentProspect = newValue.prospects[0];
          this.getProspectManager();
        }
      },
    },
  },
  async created() {
    await this.configureViewPort();
    await this.getAllData();
  },
};
</script>

<template>
  <v-app class="app-content">
    <v-main
      class="main-content"
      :class="{ mobile: $vuetify.display.smAndDown }"
    >
      <CoreHeader :current-prospect="currentProspect"></CoreHeader>
      <div class="flex-grow-1 mt-4">
        <NuxtErrorBoundary>
          <slot />
          <template #error="{ error }">
            <div class="global-error-wrapper">
              <template
                v-if="error.value.message == $t('global.errors.accessDenied')"
              >
                <h2 class="error-title">
                  {{ $t("global.errors.accessDenied") }}
                </h2>
              </template>
              <template v-else>
                <h2 class="error-title">
                  {{ $t("global.errors.unhandledError") }}
                </h2>
                <p class="error-message">{{ error }}</p>
              </template>
              <div>
                <NuxtLink
                  class="no-decoration d-inline-block"
                  to="/"
                  :external="true"
                >
                  <v-btn class="btn-cim">{{ $t("global.backToHome") }}</v-btn>
                </NuxtLink>
              </div>
            </div>
          </template>
        </NuxtErrorBoundary>
      </div>
      <CoreFooter
        class="flex-grow-0 mt-6"
        :prospect-manager="prospectManager"
      ></CoreFooter>
    </v-main>
  </v-app>
</template>

<style lang="scss">
@import "@/assets/css/variables.scss";
html {
  font-family: "Mulish", "Roboto", sans-serif;
  background: linear-gradient(45.08deg, #f7ecf3, #deecf9);
}

body {
  margin: 0px;
}

header {
  width: 100%;
}

h3 {
  font-size: 23px;
}

h5 {
  font-size: 17px;
}

h6 {
  font-size: 14px;
}

.app-content {
  background: none;
}

.main-content {
  margin: 0 10% 0 10%;
  transition: none;
  display: flex;
  min-height: 100%;
  flex-grow: 1;
  flex-direction: column;

  &.mobile {
    margin: 0 5% 0 5%;
  }
}

.global-error-wrapper {
  text-align: center;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;

  .error-title {
    margin: 20px 0;
  }
  .error-message {
    margin: 0 0 20px 0;
  }
}

/* Reusable classes accross app */

.btn-cim {
  background-color: $cim-pink;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.05em;
  line-height: 20px;
  height: 56px;
  max-width: 335px;
  border-style: solid;
  color: white;

  .v-icon {
    margin-right: 5px;
  }
}

.btn-cim-alt {
  background-color: white;
  border-color: #E05C91;
  border-width: 1px;
  color: $cim-pink;
}

.cim-link {
  font-weight: bold;
  color: #E05C91;
  cursor: pointer;
}

.cim-pink {
  font-weight: bold;
  color: #E05C91;
}

/* Override here default theme (Vuetify,...) */
.dp__theme_light {
  --dp-primary-color: $cim-pink;
}

::selection {
  background: #8ab8e5;
}

// v-card
.v-card {
  .v-card-title {
    font-size: 16px;
    font-weight: bold;
    letter-spacing: normal;
  }

  .v-card-text {
    font-size: 15px;
  }
}

// v-radio-group
.v-radio-group {
  .v-selection-control--inline {
    .v-label {
      padding-right: 10px;
    }
  }

  .v-input__control {
    .v-label {
      white-space: normal;
    }
  }

  .v-messages__message {
    text-align: left;
    padding-left: 8px;
  }
}

// v-switch
.v-switch {
  .v-input__details {
    align-items: flex-start;
    padding: 0;
  }
  .v-label {
    text-align: left;
  }
}

// v-text-field
.v-text-field {
  // Hack for iOS on dates
  input::-webkit-date-and-time-value {
    text-align: left;
  }
}

// v-textarea
.v-textarea {
  textarea {
    font-size: 13px;
    resize: none;
  }
}

// v-alert
.v-alert {
  grid-template-areas:
    "prepend content append close"
    "prepend content . .";

  .v-alert__prepend {
    align-self: center;
  }
}
</style>
